import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Progress,
  Row,
  Segmented,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  FileOutlined,
  InboxOutlined,
  PercentageOutlined,
  PartitionOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { decodeStr, formatBytes } from "../../utils";
import usePlatformStorage from "./usePlatformStorage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend
);

const { Search } = Input;
const { Text, Title } = Typography;

const chartPalette = {
  storage: "rgba(22, 119, 255, 0.78)",
  storageBorder: "rgba(22, 119, 255, 1)",
  files: "rgba(82, 196, 26, 0.72)",
  filesBorder: "rgba(82, 196, 26, 1)",
  orgs: "rgba(250, 140, 22, 0.76)",
  orgsBorder: "rgba(250, 140, 22, 1)",
};

const cardStyle = {
  height: "100%",
  borderRadius: 8,
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
};

const skeletonTitleStyle = {
  width: 240,
  height: 28,
};

const skeletonTextStyle = {
  height: 16,
};

const SkeletonText = ({ width = "100%", height = 16 }) => (
  <Skeleton.Input
    active
    block
    size="small"
    style={{
      width,
      height,
      minWidth: 0,
    }}
  />
);

const ChartSkeleton = ({ rows = 8 }) => (
  <div
    style={{
      height: Math.max(320, rows * 40),
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "12px 0",
    }}
  >
    {Array.from({ length: rows }).map((_, index) => {
      const width = `${92 - ((index * 11) % 44)}%`;

      return (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: "120px 1fr",
            gap: 12,
            alignItems: "center",
          }}
        >
          <SkeletonText width="100%" height={14} />
          <Skeleton.Input
            active
            block
            size="small"
            style={{
              width,
              height: 22,
              minWidth: 0,
            }}
          />
        </div>
      );
    })}
  </div>
);

const TableRowsSkeleton = ({ rows = 8, columns = 5 }) => (
  <Space direction="vertical" size={12} style={{ width: "100%" }}>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        style={{
          display: "grid",
          gridTemplateColumns: `minmax(180px, 1.8fr) repeat(${columns - 1}, minmax(90px, 1fr))`,
          gap: 16,
          alignItems: "center",
        }}
      >
        {Array.from({ length: columns }).map((__, columnIndex) => (
          <SkeletonText
            key={columnIndex}
            width={columnIndex === 0 ? "90%" : "70%"}
            height={columnIndex === 0 ? 18 : 14}
          />
        ))}
      </div>
    ))}
  </Space>
);

const MetricCardSkeleton = () => (
  <Card style={cardStyle}>
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <SkeletonText width="58%" height={14} />
      <SkeletonText width="74%" height={30} />
    </Space>
  </Card>
);

const StorageInsightCard = ({
  title,
  value,
  count,
  percent,
  detail,
  icon,
  color = "#1677ff",
  to,
}) => {
  return (
    <Card style={cardStyle}>
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Space align="center" size={8}>
            <span style={{ color, fontSize: 18, display: "inline-flex" }}>{icon}</span>
            <Text type="secondary">{title}</Text>
          </Space>
          {to && (
            <Link
              to={to}
              style={{
                flex: "0 0 auto",
                fontSize: 12,
                lineHeight: "18px",
                whiteSpace: "nowrap",
              }}
            >
              Vedi org <ArrowRightOutlined style={{ fontSize: 10 }} />
            </Link>
          )}
        </div>
        <Statistic
          value={value}
          formatter={(currentValue) => formatBytes(Number(currentValue || 0))}
        />
        {typeof percent === "number" && (
          <Progress
            percent={Number(percent.toFixed(1))}
            size="small"
            strokeColor={color}
            format={(currentPercent) => `${currentPercent}%`}
          />
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {count != null && `${count.toLocaleString("it-IT")} org`}
          {count != null && detail ? " · " : ""}
          {detail}
        </Text>
      </Space>
    </Card>
  );
};

const buildOrgsFiltersUrl = (filters) =>
  `/orgs?filters=${encodeURIComponent(JSON.stringify(filters))}`;

const buildOrgFilter = ({
  id,
  column,
  columnName,
  value,
  displayValue,
  operator = "equalTo",
  logicalOperator = "AND",
}) => ({
  id,
  column,
  columnName,
  value,
  displayValue,
  operator,
  logicalOperator,
});

const PlatformStorageSkeleton = () => (
  <Space direction="vertical" size="large" style={{ width: "100%" }}>
    <Card
      style={{
        borderRadius: 8,
        background:
          "linear-gradient(135deg, rgba(22, 119, 255, 0.08), rgba(250, 140, 22, 0.08))",
        border: "1px solid rgba(22, 119, 255, 0.12)",
      }}
    >
      <Row gutter={[16, 16]} justify="space-between" align="middle">
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Skeleton.Input active style={skeletonTitleStyle} />
            <SkeletonText width="80%" height={skeletonTextStyle.height} />
            <SkeletonText width="56%" height={skeletonTextStyle.height} />
          </Space>
        </Col>
        <Col xs={24} lg={8} style={{ textAlign: "right" }}>
          <Skeleton.Button active style={{ width: 104 }} />
        </Col>
      </Row>
    </Card>

    <Row gutter={[16, 16]}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Col key={index} xs={24} sm={12} xl={6}>
          <MetricCardSkeleton />
        </Col>
      ))}
    </Row>

    <Row gutter={[16, 16]}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Col key={index} xs={24} sm={12} xl={6}>
          <MetricCardSkeleton />
        </Col>
      ))}
    </Row>

    <Row gutter={[16, 16]}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Col key={index} xs={24} sm={12} xl={6}>
          <MetricCardSkeleton />
        </Col>
      ))}
    </Row>

    <Row gutter={[16, 16]}>
      <Col xs={24} lg={8}>
        <Card title={<SkeletonText width={180} height={18} />} style={{ borderRadius: 8 }}>
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <SkeletonText width="54%" height={14} />
              <SkeletonText width="72%" height={30} />
            </Space>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <SkeletonText width="48%" height={14} />
              <SkeletonText width="64%" height={30} />
            </Space>
          </Space>
        </Card>
      </Col>
      <Col xs={24} lg={16}>
        <Card title={<SkeletonText width={230} height={18} />} style={{ borderRadius: 8 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={9}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <SkeletonText width={120} height={24} />
                <SkeletonText width="66%" height={24} />
                <SkeletonText width="84%" height={14} />
              </Space>
            </Col>
            <Col xs={24} md={15}>
              <SkeletonText width="100%" height={16} />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>

    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card title={<SkeletonText width={220} height={18} />} style={{ borderRadius: 8 }}>
          <ChartSkeleton rows={8} />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title={<SkeletonText width={170} height={18} />} style={{ borderRadius: 8 }}>
          <ChartSkeleton rows={8} />
        </Card>
      </Col>
    </Row>

    <Card title={<SkeletonText width={150} height={18} />} style={{ borderRadius: 8 }}>
      <ChartSkeleton rows={7} />
    </Card>

    <Card title={<SkeletonText width={150} height={18} />} style={{ borderRadius: 8 }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} lg={10}>
            <SkeletonText width="100%" height={32} />
          </Col>
          <Col xs={24} lg={14}>
            <SkeletonText width="78%" height={32} />
          </Col>
        </Row>
        <TableRowsSkeleton rows={9} columns={6} />
      </Space>
    </Card>

    <Card title={<SkeletonText width={210} height={18} />} style={{ borderRadius: 8 }}>
      <TableRowsSkeleton rows={6} columns={5} />
    </Card>
  </Space>
);

const formatClassLabel = (className) => {
  if (!className || className === "__unknown__") {
    return "Non classificato";
  }

  return className;
};

const formatRelativeDate = (value) => {
  if (!value) return "Non disponibile";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Non disponibile";

  return `${date.toLocaleString("it-IT")} (${formatDistanceToNow(date, {
    addSuffix: true,
    locale: it,
  })})`;
};

const getBreakdownStats = (stats) => {
  if (typeof stats === "number") {
    return {
      bytes: Number(stats || 0),
      files: 0,
    };
  }

  return {
    bytes: Number(stats?.bytes || stats?.total_bytes || 0),
    files: Number(stats?.files || stats?.total_files || 0),
  };
};

const getOrgDisplayName = (record) => {
  if (!record?.orgName) return "Organizzazione non collegata";
  return decodeStr(record.orgName);
};

const formatInstanceLabel = (value) => {
  if (!value) return "Non definita";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getInstanceColor = (value) => {
  const normalized = (value || "").toLowerCase();
  if (normalized === "production") return "green";
  if (normalized === "demo") return "orange";
  if (normalized === "free") return "blue";
  return "default";
};

const getStatusColor = (value) => {
  const normalized = (value || "").toLowerCase();
  if (normalized === "attiva") return "green";
  if (normalized === "disattiva") return "red";
  return "default";
};

const parseValidDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isInactiveOrg = (record) =>
  (record.orgStatus || "").toLowerCase() !== "attiva";

const isFreeOrg = (record) =>
  (record.orgInstance || "").toLowerCase() === "free";

const isExpiredDemoOrg = (record) => {
  if ((record.orgInstance || "").toLowerCase() !== "demo") return false;

  const expirationDates = [
    parseValidDate(record.orgExpiresAt),
    parseValidDate(record.orgGiottoExpiresAt),
  ].filter(Boolean);

  return expirationDates.some((date) => date < new Date());
};

const isActiveProductionOrg = (record) =>
  (record.orgStatus || "").toLowerCase() === "attiva" &&
  (record.orgInstance || "").toLowerCase() === "production";

const getStorageSegment = (records, predicate) => {
  const matchingRecords = records.filter(predicate);

  return {
    count: matchingRecords.length,
    bytes: matchingRecords.reduce((sum, record) => sum + record.total_bytes, 0),
    files: matchingRecords.reduce((sum, record) => sum + record.total_files, 0),
  };
};

const getStorageLimitProgressColor = (percent) => {
  if (percent >= 100) return "#cf1322";
  if (percent >= 80) return "#faad14";
  return "#52c41a";
};

const buildBarChartOptions = ({ isBytes = false, label = "" } = {}) => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 250,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.raw || 0);
          const formattedValue = isBytes
            ? formatBytes(value)
            : value.toLocaleString("it-IT");
          return label ? `${label}: ${formattedValue}` : formattedValue;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        callback: (value) =>
          isBytes
            ? formatBytes(Number(value || 0), Number(value) >= 1024 ? 1 : 0)
            : Number(value || 0).toLocaleString("it-IT"),
      },
    },
    y: {
      ticks: {
        autoSkip: false,
      },
    },
  },
});

const buildClassRows = (storageRecords) => {
  const classMap = new Map();

  storageRecords.forEach((record) => {
    Object.entries(record.breakdown || {}).forEach(([className, stats]) => {
      const normalizedClassName = className || "__unknown__";
      const current = classMap.get(normalizedClassName) || {
        key: normalizedClassName,
        className: normalizedClassName,
        label: formatClassLabel(normalizedClassName),
        bytes: 0,
        files: 0,
        orgIds: new Set(),
      };
      const normalizedStats = getBreakdownStats(stats);

      current.bytes += normalizedStats.bytes;
      current.files += normalizedStats.files;
      if (record.orgId || record.objectId) {
        current.orgIds.add(record.orgId || record.objectId);
      }
      classMap.set(normalizedClassName, current);
    });
  });

  return Array.from(classMap.values())
    .map((entry) => ({
      ...entry,
      orgCount: entry.orgIds.size,
      avgBytes: entry.files > 0 ? entry.bytes / entry.files : 0,
    }))
    .sort((left, right) => {
      if (right.bytes !== left.bytes) return right.bytes - left.bytes;
      if (right.files !== left.files) return right.files - left.files;
      return left.label.localeCompare(right.label, "it");
    });
};

const buildOrgRows = (storageRecords) =>
  storageRecords
    .map((record) => {
      const classRows = buildClassRows([record]);
      const topClass = classRows[0] || null;
      const storageLimitBytes = Number(record.storageLimitBytes || 0);
      const storageLimitUsagePercent =
        storageLimitBytes > 0 ? (record.total_bytes / storageLimitBytes) * 100 : null;
      const storageLimitRemainingBytes =
        storageLimitBytes > 0 ? storageLimitBytes - record.total_bytes : null;

      return {
        key: record.objectId,
        ...record,
        storageLimitBytes: storageLimitBytes > 0 ? storageLimitBytes : null,
        storageLimitUsagePercent,
        storageLimitRemainingBytes,
        isStorageLimitExceeded:
          storageLimitRemainingBytes !== null && storageLimitRemainingBytes < 0,
        orgDisplayName: getOrgDisplayName(record),
        avgBytes: record.total_files > 0 ? record.total_bytes / record.total_files : 0,
        classCount: classRows.length,
        topClass,
      };
    })
    .sort((left, right) => {
      if (right.total_bytes !== left.total_bytes) {
        return right.total_bytes - left.total_bytes;
      }
      return left.orgDisplayName.localeCompare(right.orgDisplayName, "it");
    });

const PlatformStorage = () => {
  const {
    storageRecords,
    totalCount,
    storageLimitCount,
    totalStorageLimitBytes,
    lastUpdatedAt,
    loading,
    error,
    refetch,
  } = usePlatformStorage();
  const [searchText, setSearchText] = useState("");
  const [instanceFilter, setInstanceFilter] = useState("all");

  const orgRows = useMemo(() => buildOrgRows(storageRecords), [storageRecords]);
  const classRows = useMemo(
    () => buildClassRows(storageRecords),
    [storageRecords]
  );

  const totalBytes = useMemo(
    () => storageRecords.reduce((sum, record) => sum + record.total_bytes, 0),
    [storageRecords]
  );
  const totalFiles = useMemo(
    () => storageRecords.reduce((sum, record) => sum + record.total_files, 0),
    [storageRecords]
  );
  const averageFileSize = totalFiles > 0 ? totalBytes / totalFiles : 0;
  const inactiveStorage = useMemo(
    () => getStorageSegment(storageRecords, isInactiveOrg),
    [storageRecords]
  );
  const freeStorage = useMemo(
    () => getStorageSegment(storageRecords, isFreeOrg),
    [storageRecords]
  );
  const expiredDemoStorage = useMemo(
    () => getStorageSegment(storageRecords, isExpiredDemoOrg),
    [storageRecords]
  );
  const activeProductionStorage = useMemo(
    () => getStorageSegment(storageRecords, isActiveProductionOrg),
    [storageRecords]
  );
  const averageActiveProductionStorage =
    activeProductionStorage.count > 0
      ? activeProductionStorage.bytes / activeProductionStorage.count
      : 0;
  const rowsWithStorageLimit = useMemo(
    () => orgRows.filter((record) => Number(record.storageLimitBytes || 0) > 0),
    [orgRows]
  );
  const rowsWithoutStorageLimit = useMemo(
    () => orgRows.filter((record) => !record.storageLimitBytes),
    [orgRows]
  );
  const rowsOverStorageLimit = useMemo(
    () => rowsWithStorageLimit.filter((record) => record.isStorageLimitExceeded),
    [rowsWithStorageLimit]
  );
  const rowsNearStorageLimit = useMemo(
    () =>
      rowsWithStorageLimit.filter(
        (record) =>
          !record.isStorageLimitExceeded &&
          Number(record.storageLimitUsagePercent || 0) >= 80
      ),
    [rowsWithStorageLimit]
  );
  const storageBytesWithConfiguredLimit = useMemo(
    () => rowsWithStorageLimit.reduce((sum, record) => sum + record.total_bytes, 0),
    [rowsWithStorageLimit]
  );
  const matchedStorageLimitBytes = useMemo(
    () =>
      rowsWithStorageLimit.reduce(
        (sum, record) => sum + Number(record.storageLimitBytes || 0),
        0
      ),
    [rowsWithStorageLimit]
  );
  const storageLimitUsagePercentage =
    totalStorageLimitBytes > 0
      ? (storageBytesWithConfiguredLimit / totalStorageLimitBytes) * 100
      : 0;
  const overStorageLimitBytes = useMemo(
    () =>
      rowsOverStorageLimit.reduce(
        (sum, record) => sum + Math.abs(Number(record.storageLimitRemainingBytes || 0)),
        0
      ),
    [rowsOverStorageLimit]
  );
  const storageBytesWithoutConfiguredLimit = useMemo(
    () => rowsWithoutStorageLimit.reduce((sum, record) => sum + record.total_bytes, 0),
    [rowsWithoutStorageLimit]
  );
  const inactiveStoragePercentage =
    totalBytes > 0 ? (inactiveStorage.bytes / totalBytes) * 100 : 0;
  const freeStoragePercentage =
    totalBytes > 0 ? (freeStorage.bytes / totalBytes) * 100 : 0;
  const expiredDemoStoragePercentage =
    totalBytes > 0 ? (expiredDemoStorage.bytes / totalBytes) * 100 : 0;
  const orgsDeepLinks = useMemo(() => {
    const now = new Date().toISOString();

    return {
      inactive: buildOrgsFiltersUrl([
        buildOrgFilter({
          id: 1,
          column: "stato",
          columnName: "Stato",
          value: "attiva",
          displayValue: "attiva",
          operator: "notEqualTo",
          logicalOperator: "OR",
        }),
        buildOrgFilter({
          id: 2,
          column: "stato",
          columnName: "Stato",
          value: null,
          displayValue: "NULL",
          operator: "isNull",
          logicalOperator: "OR",
        }),
      ]),
      free: buildOrgsFiltersUrl([
        buildOrgFilter({
          id: 1,
          column: "tipo_istanza",
          columnName: "Istanza",
          value: "free",
          displayValue: "free",
        }),
      ]),
      expiredDemo: buildOrgsFiltersUrl([
        buildOrgFilter({
          id: 1,
          column: "tipo_istanza",
          columnName: "Istanza",
          value: "demo",
          displayValue: "demo",
        }),
        buildOrgFilter({
          id: 2,
          column: "data_scadenza",
          columnName: "Scadenza Eterno",
          value: now,
          displayValue: "scaduta",
          operator: "lessThan",
          logicalOperator: "OR",
        }),
        buildOrgFilter({
          id: 3,
          column: "data_scadenza_giotto",
          columnName: "Scadenza Giotto",
          value: now,
          displayValue: "scaduta",
          operator: "lessThan",
          logicalOperator: "OR",
        }),
      ]),
      activeProduction: buildOrgsFiltersUrl([
        buildOrgFilter({
          id: 1,
          column: "tipo_istanza",
          columnName: "Istanza",
          value: "production",
          displayValue: "production",
        }),
        buildOrgFilter({
          id: 2,
          column: "stato",
          columnName: "Stato",
          value: "attiva",
          displayValue: "attiva",
        }),
      ]),
    };
  }, []);
  const topClass = classRows[0] || null;
  const topClassPercentage =
    totalBytes > 0 && topClass ? (topClass.bytes / totalBytes) * 100 : 0;

  const filteredOrgRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return orgRows.filter((record) => {
      const matchesInstance =
        instanceFilter === "all" ||
        (record.orgInstance || "__unknown__") === instanceFilter;
      const matchesSearch =
        !normalizedSearch ||
        record.orgDisplayName.toLowerCase().includes(normalizedSearch) ||
        (record.orgId || "").toLowerCase().includes(normalizedSearch) ||
        (record.objectId || "").toLowerCase().includes(normalizedSearch);

      return matchesInstance && matchesSearch;
    });
  }, [instanceFilter, orgRows, searchText]);

  const instanceOptions = useMemo(() => {
    const counts = orgRows.reduce((acc, record) => {
      const key = record.orgInstance || "__unknown__";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const orderedKeys = ["production", "demo", "free", "__unknown__"].filter(
      (key) => counts[key]
    );
    const extraKeys = Object.keys(counts)
      .filter((key) => !orderedKeys.includes(key))
      .sort((left, right) => left.localeCompare(right, "it"));

    return [
      {
        label: `Tutte (${orgRows.length})`,
        value: "all",
      },
      ...[...orderedKeys, ...extraKeys].map((key) => ({
        label: `${formatInstanceLabel(key === "__unknown__" ? null : key)} (${counts[key]})`,
        value: key,
      })),
    ];
  }, [orgRows]);

  const topOrgRows = useMemo(() => orgRows.slice(0, 20), [orgRows]);
  const topClassRows = useMemo(() => classRows.slice(0, 20), [classRows]);
  const topOrgChartHeight = Math.max(320, topOrgRows.length * 36);
  const classChartHeight = Math.max(320, topClassRows.length * 42);

  const topOrgChartData = useMemo(
    () => ({
      labels: topOrgRows.map((entry) => entry.orgDisplayName),
      datasets: [
        {
          label: "Spazio occupato",
          data: topOrgRows.map((entry) => entry.total_bytes),
          backgroundColor: chartPalette.orgs,
          borderColor: chartPalette.orgsBorder,
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 24,
        },
      ],
    }),
    [topOrgRows]
  );

  const classBytesChartData = useMemo(
    () => ({
      labels: topClassRows.map((entry) => entry.label),
      datasets: [
        {
          label: "Spazio occupato",
          data: topClassRows.map((entry) => entry.bytes),
          backgroundColor: chartPalette.storage,
          borderColor: chartPalette.storageBorder,
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 28,
        },
      ],
    }),
    [topClassRows]
  );

  const classFilesChartData = useMemo(
    () => ({
      labels: topClassRows.map((entry) => entry.label),
      datasets: [
        {
          label: "File",
          data: topClassRows.map((entry) => entry.files),
          backgroundColor: chartPalette.files,
          borderColor: chartPalette.filesBorder,
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 28,
        },
      ],
    }),
    [topClassRows]
  );

  const orgColumns = useMemo(
    () => [
      {
        title: "Organizzazione",
        dataIndex: "orgDisplayName",
        key: "orgDisplayName",
        fixed: "left",
        sorter: (left, right) =>
          left.orgDisplayName.localeCompare(right.orgDisplayName, "it"),
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            {record.orgId ? (
              <Link to={`/orgs/${record.orgId}/storage`}>
                <Text strong>{record.orgDisplayName}</Text>
              </Link>
            ) : (
              <Text strong>{record.orgDisplayName}</Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.orgId || record.objectId}
            </Text>
          </Space>
        ),
      },
      {
        title: "Istanza",
        dataIndex: "orgInstance",
        key: "orgInstance",
        width: 132,
        filters: instanceOptions
          .filter((option) => option.value !== "all")
          .map((option) => ({
            text: option.label.replace(/\s\(\d+\)$/, ""),
            value: option.value,
          })),
        onFilter: (value, record) =>
          (record.orgInstance || "__unknown__") === value,
        render: (value) => (
          <Tag color={getInstanceColor(value)}>
            {formatInstanceLabel(value)}
          </Tag>
        ),
      },
      {
        title: "Stato",
        dataIndex: "orgStatus",
        key: "orgStatus",
        width: 116,
        render: (value) =>
          value ? <Tag color={getStatusColor(value)}>{value}</Tag> : "-",
      },
      {
        title: "Spazio",
        dataIndex: "total_bytes",
        key: "total_bytes",
        defaultSortOrder: "descend",
        sorter: (left, right) => left.total_bytes - right.total_bytes,
        render: (value) => <Text strong>{formatBytes(value)}</Text>,
      },
      {
        title: "Limite",
        dataIndex: "storageLimitBytes",
        key: "storageLimitBytes",
        sorter: (left, right) =>
          Number(left.storageLimitBytes || 0) - Number(right.storageLimitBytes || 0),
        render: (value) =>
          value ? <Text>{formatBytes(value)}</Text> : <Tag color="default">N/D</Tag>,
      },
      {
        title: "Uso limite",
        key: "storageLimitUsagePercent",
        responsive: ["md"],
        sorter: (left, right) =>
          Number(left.storageLimitUsagePercent || 0) -
          Number(right.storageLimitUsagePercent || 0),
        render: (_, record) => {
          if (record.storageLimitUsagePercent === null) return "-";

          const percent = Number(record.storageLimitUsagePercent || 0);

          return (
            <Progress
              percent={Math.min(Number(percent.toFixed(1)), 100)}
              size="small"
              strokeColor={getStorageLimitProgressColor(percent)}
              status={percent >= 100 ? "exception" : "normal"}
              format={() => `${percent.toFixed(1)}%`}
            />
          );
        },
      },
      {
        title: "Margine",
        key: "storageLimitRemainingBytes",
        responsive: ["lg"],
        sorter: (left, right) =>
          Number(left.storageLimitRemainingBytes ?? Number.MAX_SAFE_INTEGER) -
          Number(right.storageLimitRemainingBytes ?? Number.MAX_SAFE_INTEGER),
        render: (_, record) => {
          if (record.storageLimitRemainingBytes === null) return "-";

          const remainingBytes = Number(record.storageLimitRemainingBytes || 0);
          const isExceeded = remainingBytes < 0;

          return (
            <Text type={isExceeded ? "danger" : undefined}>
              {isExceeded
                ? `${formatBytes(Math.abs(remainingBytes))} oltre`
                : `${formatBytes(remainingBytes)} liberi`}
            </Text>
          );
        },
      },
      {
        title: "File",
        dataIndex: "total_files",
        key: "total_files",
        sorter: (left, right) => left.total_files - right.total_files,
        render: (value) => Number(value || 0).toLocaleString("it-IT"),
      },
      {
        title: "Media / file",
        dataIndex: "avgBytes",
        key: "avgBytes",
        responsive: ["lg"],
        sorter: (left, right) => left.avgBytes - right.avgBytes,
        render: (value) => formatBytes(value),
      },
      {
        title: "Classi",
        dataIndex: "classCount",
        key: "classCount",
        responsive: ["lg"],
        sorter: (left, right) => left.classCount - right.classCount,
        render: (value) => Number(value || 0).toLocaleString("it-IT"),
      },
      {
        title: "Classe prevalente",
        key: "topClass",
        responsive: ["xl"],
        render: (_, record) =>
          record.topClass ? (
            <Space direction="vertical" size={0}>
              <Tag color="blue">{record.topClass.label}</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatBytes(record.topClass.bytes)}
              </Text>
            </Space>
          ) : (
            "-"
          ),
      },
      {
        title: "% piattaforma",
        key: "share",
        responsive: ["md"],
        sorter: (left, right) => left.total_bytes - right.total_bytes,
        render: (_, record) => {
          const percentage =
            totalBytes > 0 ? (record.total_bytes / totalBytes) * 100 : 0;
          return `${percentage.toFixed(1)}%`;
        },
      },
      {
        title: "Aggiornato",
        dataIndex: "updatedAt",
        key: "updatedAt",
        responsive: ["xl"],
        sorter: (left, right) =>
          new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime(),
        render: (value) => formatRelativeDate(value),
      },
    ],
    [instanceOptions, totalBytes]
  );

  const classColumns = useMemo(
    () => [
      {
        title: "Classe",
        dataIndex: "label",
        key: "label",
        render: (_, record) => (
          <Tag color={record.className === "__unknown__" ? "default" : "blue"}>
            {record.label}
          </Tag>
        ),
      },
      {
        title: "Spazio",
        dataIndex: "bytes",
        key: "bytes",
        defaultSortOrder: "descend",
        sorter: (left, right) => left.bytes - right.bytes,
        render: (value) => formatBytes(value),
      },
      {
        title: "File",
        dataIndex: "files",
        key: "files",
        sorter: (left, right) => left.files - right.files,
        render: (value) => Number(value || 0).toLocaleString("it-IT"),
      },
      {
        title: "Org",
        dataIndex: "orgCount",
        key: "orgCount",
        sorter: (left, right) => left.orgCount - right.orgCount,
        render: (value) => Number(value || 0).toLocaleString("it-IT"),
      },
      {
        title: "Media / file",
        dataIndex: "avgBytes",
        key: "avgBytes",
        responsive: ["md"],
        sorter: (left, right) => left.avgBytes - right.avgBytes,
        render: (value) => formatBytes(value),
      },
      {
        title: "% sul totale",
        key: "share",
        render: (_, record) => {
          const percentage =
            totalBytes > 0 ? (record.bytes / totalBytes) * 100 : 0;
          return `${percentage.toFixed(1)}%`;
        },
      },
    ],
    [totalBytes]
  );

  if (loading) {
    return <PlatformStorageSkeleton />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Impossibile caricare lo storage piattaforma"
        description={error}
      />
    );
  }

  if (storageRecords.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nessun dato di storage disponibile a livello piattaforma."
        >
          <Button icon={<ReloadOutlined />} onClick={refetch}>
            Ricarica
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        style={{
          borderRadius: 8,
          background:
            "linear-gradient(135deg, rgba(22, 119, 255, 0.08), rgba(250, 140, 22, 0.08))",
          border: "1px solid rgba(22, 119, 255, 0.12)",
        }}
      >
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} lg={16}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Title level={3} style={{ margin: 0 }}>
                Storage piattaforma
              </Title>
              <Text type="secondary">
                Vista globale dello spazio occupato da tutte le organizzazioni censite.
              </Text>
              <Text type="secondary">
                Ultimo aggiornamento aggregato: {formatRelativeDate(lastUpdatedAt)}
              </Text>
            </Space>
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: "right" }}>
            <Button icon={<ReloadOutlined />} onClick={refetch}>
              Ricarica
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card style={cardStyle}>
            <Statistic
              title="Spazio occupato"
              value={totalBytes}
              formatter={(value) => formatBytes(Number(value || 0))}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={cardStyle}>
            <Statistic
              title="File censiti"
              value={totalFiles}
              formatter={(value) => Number(value || 0).toLocaleString("it-IT")}
              prefix={<FileOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={cardStyle}>
            <Statistic
              title="Organizzazioni"
              value={orgRows.length}
              suffix={totalCount > orgRows.length ? `/ ${totalCount}` : null}
              formatter={(value) => Number(value || 0).toLocaleString("it-IT")}
              prefix={<ApartmentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={cardStyle}>
            <Statistic
              title="Classi aggregate"
              value={classRows.length}
              formatter={(value) => Number(value || 0).toLocaleString("it-IT")}
              prefix={<PartitionOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Limite storage configurato"
            value={totalStorageLimitBytes}
            count={storageLimitCount}
            detail={`${formatBytes(matchedStorageLimitBytes)} su org con storage`}
            icon={<DatabaseOutlined />}
            color="#13a8a8"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Uso su limiti storage"
            value={storageBytesWithConfiguredLimit}
            count={rowsWithStorageLimit.length}
            percent={storageLimitUsagePercentage}
            detail={`${formatBytes(totalStorageLimitBytes)} limite totale`}
            icon={<PercentageOutlined />}
            color={getStorageLimitProgressColor(storageLimitUsagePercentage)}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Sforamento storage"
            value={overStorageLimitBytes}
            count={rowsOverStorageLimit.length}
            percent={
              rowsWithStorageLimit.length > 0
                ? (rowsOverStorageLimit.length / rowsWithStorageLimit.length) * 100
                : 0
            }
            detail={`${rowsNearStorageLimit.length.toLocaleString("it-IT")} org oltre l'80%`}
            icon={<StopOutlined />}
            color="#cf1322"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Storage senza limite"
            value={storageBytesWithoutConfiguredLimit}
            count={rowsWithoutStorageLimit.length}
            percent={totalBytes > 0 ? (storageBytesWithoutConfiguredLimit / totalBytes) * 100 : 0}
            detail="org senza max_cloud_storage_bytes"
            icon={<InboxOutlined />}
            color="#722ed1"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Spazio org non attive"
            value={inactiveStorage.bytes}
            count={inactiveStorage.count}
            percent={inactiveStoragePercentage}
            detail={`${inactiveStorage.files.toLocaleString("it-IT")} file`}
            icon={<StopOutlined />}
            color="#cf1322"
            to={orgsDeepLinks.inactive}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Spazio org free"
            value={freeStorage.bytes}
            count={freeStorage.count}
            percent={freeStoragePercentage}
            detail={`${freeStorage.files.toLocaleString("it-IT")} file`}
            icon={<PercentageOutlined />}
            color="#1677ff"
            to={orgsDeepLinks.free}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Spazio demo scadute"
            value={expiredDemoStorage.bytes}
            count={expiredDemoStorage.count}
            percent={expiredDemoStoragePercentage}
            detail={`${expiredDemoStorage.files.toLocaleString("it-IT")} file`}
            icon={<ExperimentOutlined />}
            color="#d46b08"
            to={orgsDeepLinks.expiredDemo}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StorageInsightCard
            title="Media org attive production"
            value={averageActiveProductionStorage}
            count={activeProductionStorage.count}
            detail={`${formatBytes(activeProductionStorage.bytes)} totali`}
            icon={<InboxOutlined />}
            color="#13a8a8"
            to={orgsDeepLinks.activeProduction}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title="Medie piattaforma" style={{ borderRadius: 8, height: "100%" }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Statistic
                title="Media org attive production"
                value={averageActiveProductionStorage}
                formatter={(value) => formatBytes(Number(value || 0))}
                prefix={<InboxOutlined />}
              />
              <Statistic
                title="Dimensione media file"
                value={averageFileSize}
                formatter={(value) => formatBytes(Number(value || 0))}
                prefix={<FileOutlined />}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          {topClass ? (
            <Card title="Classe prevalente piattaforma" style={{ borderRadius: 8 }}>
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={9}>
                  <Space direction="vertical" size={4}>
                    <Tag color="blue" style={{ width: "fit-content", marginInlineEnd: 0 }}>
                      {topClass.label}
                    </Tag>
                    <Text strong>{formatBytes(topClass.bytes)}</Text>
                    <Text type="secondary">
                      {topClass.files.toLocaleString("it-IT")} file in{" "}
                      {topClass.orgCount.toLocaleString("it-IT")} org
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} md={15}>
                  <Progress
                    percent={Number(topClassPercentage.toFixed(1))}
                    strokeColor="#1677ff"
                    format={(percent) => `${percent}% del totale`}
                  />
                </Col>
              </Row>
            </Card>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Il dettaglio per classe non è ancora disponibile."
            />
          )}
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="Top organizzazioni per spazio" style={{ borderRadius: 8 }}>
            <div style={{ height: topOrgChartHeight }}>
              <Chart
                type="bar"
                data={topOrgChartData}
                options={buildBarChartOptions({
                  isBytes: true,
                  label: "Spazio occupato",
                })}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="Spazio per classe" style={{ borderRadius: 8 }}>
            <div style={{ height: classChartHeight }}>
              <Chart
                type="bar"
                data={classBytesChartData}
                options={buildBarChartOptions({
                  isBytes: true,
                  label: "Spazio occupato",
                })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="File per classe" style={{ borderRadius: 8 }}>
            <div style={{ height: classChartHeight }}>
              <Chart
                type="bar"
                data={classFilesChartData}
                options={buildBarChartOptions({
                  isBytes: false,
                  label: "File",
                })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title="Organizzazioni"
        style={{ borderRadius: 8 }}
        extra={
          <Text type="secondary">
            {filteredOrgRows.length.toLocaleString("it-IT")} /{" "}
            {orgRows.length.toLocaleString("it-IT")}
          </Text>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} lg={10}>
              <Search
                allowClear
                placeholder="Cerca organizzazione o ID"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Col>
            <Col xs={24} lg={14}>
              <Segmented
                options={instanceOptions}
                value={instanceFilter}
                onChange={setInstanceFilter}
                style={{ maxWidth: "100%", overflowX: "auto" }}
              />
            </Col>
          </Row>

          <Table
            rowKey="key"
            dataSource={filteredOrgRows}
            columns={orgColumns}
            size="small"
            scroll={{ x: 1280 }}
            pagination={{
              pageSize: 25,
              showSizeChanger: true,
              showTotal: (total) => `${total.toLocaleString("it-IT")} organizzazioni`,
            }}
          />
        </Space>
      </Card>

      <Card title="Dettaglio classi aggregate" style={{ borderRadius: 8 }}>
        <Table
          rowKey="key"
          dataSource={classRows}
          columns={classColumns}
          size="small"
          pagination={false}
        />
      </Card>
    </Space>
  );
};

export default PlatformStorage;
